<script lang="ts">
	import { clone, type JsonValue } from "@bufbuild/protobuf";
	import { Tooltip } from "flowbite-svelte";
	import { A, Label, Select } from "flowbite-svelte-v1";
	import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import Toggle from "#lib/components/Toggle.svelte";
	import AppearanceItem from "#lib/components/flow/editor/appearance/AppearanceItem.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { BORDER_RADIUS_VALUES, FORM_CONTAINER_CONTROLS, FORM_INPUT_CONTROLS, FORM_MISC_CONTROLS, FORM_SPACING_VALUES, FORM_SUBMIT_CONTROLS, FORM_TERMS_CONTROLS, FORM_TEXT_CONTROLS, FORM_TYPOGRAPHY_CONTROLS, type FormStyleControl } from "#lib/components/flow/editor/appearance/constants.js";
	import { locate } from "#lib/components/page/editing/utils.js";
	import { formLayout } from "#lib/components/page/formLayout.js";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { setStructValue, setValue, stringValue, structValue } from "#lib/struct.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		onChange: (value?: JsonValue) => void;
	}

	let { id, page, onChange }: Props = $props();

	const componentStyle = $derived(locate($page.components, id)?.node?.style as JsonObject | undefined);
	const stickySubmitButton = $derived(formLayout($page, id).stickySubmitButton);

	// The stored override for a CSS key, if any. Undefined falls back to the control's default.
	function rawValue(control: FormStyleControl): string | undefined {
		return stringValue(componentStyle, control.styleKey);
	}

	function valueFor(control: FormStyleControl): string {
		return rawValue(control) ?? control.default;
	}

	// Show the reset link only when the stored value actually differs from the default. A brand new form
	// is seeded with the default container styles, so a plain "has a stored value" check would wrongly
	// offer "Reset to default" on values that already match it.
	function isOverridden(control: FormStyleControl): boolean {
		const raw = rawValue(control);
		return raw !== undefined && raw.toLowerCase() !== control.default.toLowerCase();
	}

	function writeFormStyle(styleKey: string, value: string | undefined) {
		const node = locate($page.components, id)?.node;
		if (!node) return;
		const style = { ...((node.style as JsonObject | undefined) ?? {}) } as JsonObject;
		if (value) {
			style[styleKey] = value;
		} else {
			delete style[styleKey];
		}
		node.style = style;
		$page = clone(PageSchema, $page);
	}

	function writeFormConfig(key: string, value: boolean) {
		if (!$page.config) $page.config = {};
		const config = { ...(structValue($page.config, id) ?? {}) } as JsonObject;
		setValue(config, key, value);
		setStructValue($page.config, id, config);
		$page = clone(PageSchema, $page);
		onChange?.();
	}

	function onColorInput(control: FormStyleControl, value: string) {
		if (value) {
			writeFormStyle(control.styleKey, value);
		}
	}

	function handleSelect(e: Event, control: FormStyleControl) {
		const value = (e.target as HTMLSelectElement).value;
		writeFormStyle(control.styleKey, value || undefined);
		onChange?.();
	}

	function resetControl(control: FormStyleControl) {
		writeFormStyle(control.styleKey, undefined);
		onChange?.();
	}
</script>

{#snippet controlRow(control: FormStyleControl)}
	<div class="flex flex-col gap-1">
		<div class="flex items-center gap-1">
			<Label for="{id}-{control.styleKey}" class="font-medium">{control.label}</Label>
			{#if control.tooltip}
				<Icon name="information-circle" class="h-3.5 w-3.5 text-gray-400" />
				<Tooltip placement="bottom" class="z-10 max-w-64 bg-gray-800 font-normal text-white">{control.tooltip}</Tooltip>
			{/if}
		</div>
		{#if control.type === "color"}
			<ColorInputV2 id="{id}-{control.styleKey}" allowTransparent={control.allowTransparent} display="inline" resetColor={control.default} resetLabel="Reset to default" hideResetAtColor value={valueFor(control)} overridden={isOverridden(control)} onReset={() => resetControl(control)} oninput={value => onColorInput(control, value)} onchange={value => onChange?.(value)} />
		{:else}
			<Select id="{id}-{control.styleKey}" classes={{ select: "truncate" }} items={control.type === "radius" ? BORDER_RADIUS_VALUES : FORM_SPACING_VALUES} value={valueFor(control)} onchange={e => handleSelect(e, control)} />
			{#if isOverridden(control)}
				<div class="flex justify-end">
					<A onclick={() => resetControl(control)} class="text-xs font-medium normal-case no-underline hover:no-underline">Reset to default</A>
				</div>
			{/if}
		{/if}
	</div>
{/snippet}

<!-- A labelled block of controls inside a card. Multiple sub-groups share one AppearanceItem when a
	section (Content, Input) covers several distinct parts. -->
{#snippet subGroup(label: string, controls: FormStyleControl[])}
	<div class="flex flex-col gap-3">
		<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">{label}</P>
		<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
			{#each controls as control (control.styleKey)}
				{@render controlRow(control)}
			{/each}
		</div>
	</div>
{/snippet}

<div class="flex flex-col gap-6">
	<AppearanceItem title="Container" appearanceLabel="">
		<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
			{#each FORM_CONTAINER_CONTROLS as control (control.styleKey)}
				{@render controlRow(control)}
			{/each}
		</div>
	</AppearanceItem>
	<AppearanceItem title="Content" appearanceLabel="">
		{@render subGroup("Typography", FORM_TYPOGRAPHY_CONTROLS)}
		{@render subGroup("Terms & conditions", FORM_TERMS_CONTROLS)}
	</AppearanceItem>
	<AppearanceItem title="Form Elements" appearanceLabel="">
		{@render subGroup("Labels", FORM_TEXT_CONTROLS)}
		{@render subGroup("Inputs", FORM_INPUT_CONTROLS)}
		{@render subGroup("Submit button", FORM_SUBMIT_CONTROLS)}
		{@render subGroup("Misc", FORM_MISC_CONTROLS)}
	</AppearanceItem>
	<AppearanceItem title="Layout & behavior" appearanceLabel="">
		<div class="flex flex-col gap-1">
			<Toggle size="small" class="self-start" checked={stickySubmitButton} onchange={e => writeFormConfig("stickySubmitButton", (e.target as HTMLInputElement).checked)}>
				<span class="text-sm font-medium text-gray-900">Sticky submit button</span>
			</Toggle>
			<P class="text-xs text-gray-500">Pin the submit button to the bottom of the screen as visitors scroll. Turn off to let it sit below the fields.</P>
		</div>
	</AppearanceItem>
</div>
